/* PLP Bundle Entry — promo tile linking into the bundle builder
 * -----------------------------------------------------------------------------
 * Companion module for sections/plp.liquid. Injects a single non-product tile
 * ("Build your wellness bundle" / "Customise") into the collection grid on the
 * handles listed in the section settings.
 *
 * Why injection rather than a real product: the PLP grid is painted entirely by
 * the compiled Preact bundle (assets/section-plp-native.js), which fetches
 * products as JSON and has no concept of a non-product tile. That bundle is
 * built outside this repo and cannot be rebuilt here, so the tile is added to
 * the rendered DOM afterwards. The alternative — creating a real Shopify
 * product — would leak into search, feeds and the sitemap, its Add button would
 * put a phantom line in the cart, and its button label could not be changed.
 *
 * The grid is re-rendered whenever the customer filters, sorts or pages, which
 * discards the tile. A MutationObserver re-inserts it, and the
 * `data-nh-plp-entry` marker keeps that idempotent.
 *
 * Unlike a product card, the tile is a single bordered blush panel: pill badge
 * top-right, product image and title centred, CTA, then a secondary link to
 * the pre-made bundles collection. The panel is a plain div rather than one
 * big anchor — with two destinations, wrapping everything in a link would mean
 * nesting anchors, which is invalid.
 */
(function () {
  'use strict';

  var TILE_MARKER = 'data-nh-plp-entry';

  function currentHandle(doc) {
    var host = doc.querySelector('plp-native');
    return host ? (host.getAttribute('handle') || '') : '';
  }

  /* The grid is the parent of the product cards. Deriving it from a card rather
   * than matching utility classes means a Tailwind change upstream can't break
   * the lookup. */
  function findGrid(doc) {
    var card = doc.querySelector('.plp-card:not([' + TILE_MARKER + '])');
    return card ? card.parentElement : null;
  }

  /* Panel order: badge (top-right), image + title (centred, absorbing the
   * slack), CTA, secondary link. The CTA is an anchor rather than a button —
   * a <button> would read as add-to-cart to both the shopper and the compiled
   * card JS. */
  function buildTile(doc, state) {
    var tile = doc.createElement('div');
    tile.className = 'flex flex-col plp-card w-full nh-plp-entry';
    tile.setAttribute(TILE_MARKER, '');

    var panel = doc.createElement('div');
    panel.className = 'nh-plp-entry__panel';

    if (state.badge) {
      var badge = doc.createElement('span');
      badge.className = 'nh-plp-entry__badge';
      badge.textContent = state.badge;
      panel.appendChild(badge);
    }

    /* The image gets its own box, which takes `flex: 1 1 0` so it absorbs the
     * leftover height WITHOUT its own intrinsic size feeding back into the
     * panel. The title must stay outside it: inside, the same rule would
     * squeeze the text, and a tall portrait image would stretch the grid row. */
    var media = doc.createElement('span');
    media.className = 'nh-plp-entry__media';
    if (state.image) {
      var img = doc.createElement('img');
      img.className = 'nh-plp-entry__img';
      img.src = state.image;
      // Decorative: the title beneath it already carries the meaning.
      img.alt = '';
      img.setAttribute('aria-hidden', 'true');
      img.setAttribute('loading', 'lazy');
      media.appendChild(img);
    }
    panel.appendChild(media);

    var title = doc.createElement('span');
    title.className = 'nh-plp-entry__title font-bold uppercase text-grey-900';
    title.textContent = state.title;
    panel.appendChild(title);

    var cta = doc.createElement('a');
    cta.className = 'button-primary plp-card-button nh-plp-entry__cta';
    cta.href = state.url;
    cta.textContent = state.button;
    panel.appendChild(cta);

    /* Second destination: the pre-made bundles collection. The prefix stays
     * OUTSIDE the anchor so only the label underlines, matching the reference
     * ("Or Shop pre-made bundles"). */
    if (state.secondaryText && state.secondaryUrl) {
      var secondary = doc.createElement('span');
      secondary.className = 'nh-plp-entry__secondary';
      if (state.secondaryPrefix) {
        secondary.appendChild(doc.createTextNode(state.secondaryPrefix + ' '));
      }
      var secondaryLink = doc.createElement('a');
      secondaryLink.href = state.secondaryUrl;
      secondaryLink.textContent = state.secondaryText;
      secondary.appendChild(secondaryLink);
      panel.appendChild(secondary);
    }

    tile.appendChild(panel);
    return tile;
  }

  function mount(doc, state) {
    var grid = findGrid(doc);
    if (!grid) return false;
    if (grid.querySelector('[' + TILE_MARKER + ']')) return true;

    var tile = buildTile(doc, state);
    var cards = grid.children;
    // position is 1-based for merchants; clamp so an over-large value lands last.
    var index = Math.max(0, Math.min(state.position - 1, cards.length));
    if (index >= cards.length) grid.appendChild(tile);
    else grid.insertBefore(tile, cards[index]);
    return true;
  }

  function init(doc, win) {
    var stateEl = doc.querySelector('[data-nh-plp-entry-state]');
    if (!stateEl) return;

    var state;
    try { state = JSON.parse(stateEl.textContent); } catch (e) { return; }
    if (!state.enabled || !state.url || !state.title) return;

    var handle = currentHandle(doc);
    if (!handle || state.collections.indexOf(handle) === -1) return;

    mount(doc, state);

    /* Filtering, sorting and paging all replace the grid's contents, which
     * discards the tile. Re-mount whenever the subtree changes.
     *
     * The observer is DISCONNECTED around the insert. Inserting the tile is
     * itself a subtree mutation, so an always-on observer would re-enter and
     * spin forever if the idempotency marker ever stopped matching. Rather than
     * let that guard be the only thing between us and a hung tab, the loop is
     * made structurally impossible here. */
    var host = doc.querySelector('plp-native');
    if (!host || !win.MutationObserver) return;

    var config = { childList: true, subtree: true };
    var scheduled = false;
    var observer = new win.MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      win.setTimeout(function () {
        scheduled = false;
        observer.disconnect();
        try { mount(doc, state); } finally { observer.observe(host, config); }
      }, 0);
    });
    observer.observe(host, config);
  }

  function start() { init(document, window); }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  /* Exposed for the jsdom tests, which drive a document other than this one. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = { init: init, mount: mount, buildTile: buildTile };
  }
})();
